/**
 * @file: LeadRemindBody.tsx
 *
 * `OrgLeadRemind` — the per-lead reminder that lands in the staff member's
 * pinned 商机提醒 inbox thread. Renders the lead snapshot the payload carries
 * (plate / vehicle / subject, status + intention chips, target date, last
 * reach, and the active-item list) with a "查看线索" deep-link to the lead
 * detail. Decoupled from the maintenance-plan reminder body.
 */

import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import { DecisionChip } from '@euler/app/flows/leads/pages/detail/components/covered-items-section/DecisionChip';
import { IntentionChip } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { leadStatusTone } from '@euler/app/flows/leads/pages/list/components/lead-card';
import { StatusPill } from '@euler/components/StatusPill';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadItemSnapshot, OrgLeadRemindFeedPayload } from '@euler/model/feed';
import { LeadStatusLabel } from '@euler/model/lead/Lead';
import { leadReachSummaryFromData } from '@euler/model/lead/LeadActivity';
import { formatDateTime } from '@euler/utils/formatters/datetime';
import { usePersistFn } from '@euler/utils/hooks';
import { FC, memo, useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { detailTypeName, serviceSubjectTypeName } from './common/labels';
import { LabelRow } from './common/primitives';

export const LeadRemindBody: FC<{
  p: OrgLeadRemindFeedPayload;
  /** Recipient-row id of this feed message (for manual mark-read). */
  messageId?: string;
  /** Read timestamp — absent/null means unread. */
  readAt?: string | null;
  /** Mark this message read once the user acts on it. */
  onMarkRead?: (recipientId: string) => void;
}> = ({ p, messageId, readAt, onMarkRead }) => {
  const { rpx } = useResponsiveContext();
  const navigation = useAppNavigation();
  const markRead = usePersistFn(() => {
    if (messageId) onMarkRead?.(messageId);
  });

  // Open the lead detail as an app-level route (`_lead`) so it pushes onto the
  // ROOT stack — going back returns to this feed thread, not the lead center.
  const openLeadDetail = useCallback(() => {
    navigation.push('_lead', { id: p.refs.leadId });
  }, [navigation, p.refs.leadId]);

  const vehicleDisplay = p.vehicleName ?? p.vehicleModel;
  // 8-char Chinese plates are new-energy (NEV); mirror the green accent used
  // across the app for the EV badge.
  const isNewEnergyPlate = p.licensePlateNo.length === 8;
  const plateColor = isNewEnergyPlate ? '#40c779' : '#1f80ff';

  return (
    <View>
      {/* License plate + vehicle is the header line — the item names are
          already listed below, so the subject would be redundant here. */}
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-wrap: wrap;
        `}
      >
        <View
          css={`
            padding: ${rpx(3)}px ${rpx(8)}px;
            border-radius: ${rpx(5)}px;
            border-width: 1px;
            border-color: ${plateColor};
          `}
        >
          <Typography.Text
            size={rpx(17)}
            color={plateColor}
            bold
            letterSpacing={1}
          >
            {p.licensePlateNo}
          </Typography.Text>
        </View>
        {vehicleDisplay ? (
          <Typography.Text ml={rpx(8)} size={rpx(13)} color="#374151" medium>
            {vehicleDisplay}
          </Typography.Text>
        ) : null}
        {p.attempt > 1 ? (
          <Typography.Text ml={rpx(8)} size={rpx(12)} color="#8e8e93">
            第 {p.attempt} 次提醒
          </Typography.Text>
        ) : null}
      </View>

      {/* Status + intention chips on their own line below the header. */}
      <View
        css={`
          flex-direction: row;
          align-items: center;
          flex-wrap: wrap;
          margin-top: ${rpx(8)}px;
        `}
      >
        <StatusPill
          label={LeadStatusLabel[p.status]}
          tone={leadStatusTone(p.status)}
        />
        <View style={{ marginLeft: rpx(6) }}>
          <IntentionChip intention={p.intention} />
        </View>
      </View>

      <View style={{ marginTop: rpx(10) }}>
        <LabelRow label="目标进店" value={p.targetServiceDate} />
        {p.customerName ? (
          <LabelRow label="车主信息" value={p.customerName} />
        ) : null}
        {p.assigneeName ? (
          <LabelRow label="服务顾问" value={p.assigneeName} />
        ) : null}
        {p.lastReachAt ? (
          <LabelRow
            label="上次联系"
            value={[
              formatDateTime(p.lastReachAt),
              leadReachSummaryFromData(p.lastReachMeta),
            ]
              .filter((s): s is string => !!s)
              .join(' · ')}
          />
        ) : null}
      </View>

      {p.items.length > 0 ? (
        <View style={{ marginTop: rpx(18) }}>
          <Typography.Text size={rpx(12)} color="#6b7280" medium>
            相关项目
          </Typography.Text>
          {/* Top + bottom rules bracket the list (dividers sit between rows). */}
          <View
            style={{
              marginTop: rpx(4),
              borderColor: '#f0f0f0',
              borderTopWidth: 1,
              borderBottomWidth: 1,
            }}
          >
            {p.items.map((item, index) => (
              <LeadItemRow key={item.id} item={item} first={index === 0} />
            ))}
          </View>
        </View>
      ) : null}

      <View
        css={`
          flex-direction: row;
          align-items: center;
          margin-top: ${rpx(12)}px;
        `}
      >
        <TouchableOpacity
          onPress={openLeadDetail}
          activeOpacity={0.7}
          css={`
            padding: ${rpx(7)}px ${rpx(14)}px;
            border-radius: ${rpx(6)}px;
            background-color: #0066ff;
          `}
        >
          <Typography.Text size={rpx(13)} color="#fff" medium>
            查看商机 →
          </Typography.Text>
        </TouchableOpacity>
        {onMarkRead && messageId && !readAt ? (
          <MarkAsReadButton onPress={markRead} />
        ) : null}
      </View>
    </View>
  );
};

/**
 * Secondary outline CTA that clears this reminder from the inbox. The leads
 * thread is manual-read (like maintenance-plans) so opening it doesn't wipe
 * every lead's badge — each reminder goes read only on this explicit action.
 */
const MarkAsReadButton: FC<{ onPress: () => void }> = memo(({ onPress }) => {
  const { rpx } = useResponsiveContext();
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      css={`
        margin-left: ${rpx(16)}px;
        padding: ${rpx(6)}px ${rpx(14)}px;
        border-radius: ${rpx(6)}px;
        border-width: 1px;
        border-color: #d1d5db;
      `}
    >
      <Typography.Text size={rpx(13)} color="#6b7280" medium>
        标记为已读
      </Typography.Text>
    </TouchableOpacity>
  );
});

/**
 * One covered-item row, laid out like the lead detail's 待服务项目 list: the
 * item name on the left, its type chip + decision chip right-aligned, with a
 * hairline divider between rows.
 */
const LeadItemRow: FC<{ item: LeadItemSnapshot; first: boolean }> = ({
  item,
  first,
}) => {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: rpx(5),
        borderTopWidth: first ? 0 : 1,
        borderTopColor: '#f0f0f0',
      }}
    >
      {/* Name + its type chip inline on the left; decision chip flush-right. */}
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          paddingRight: rpx(10),
        }}
      >
        <Typography.Text size={rpx(13)} color="#1f2937" medium>
          {item.customLabel?.trim() || item.name}
        </Typography.Text>
        {/* Type is a plain muted label next to the name. Catalog items carry
            the type in subjectType (plan-detail `type` is empty for them). */}
        <Typography.Text ml={rpx(8)} size={rpx(10)} color="#9ca3af">
          {item.subjectType != null
            ? serviceSubjectTypeName(item.subjectType)
            : detailTypeName(item.type)}
        </Typography.Text>
      </View>
      <View style={{ width: rpx(76), alignItems: 'flex-end' }}>
        {/* Always show the decision (待确认 when unset), mirroring the lead
            detail's covered-item list. Reminders fire only for active leads,
            so `terminal={false}` renders the undecided state as amber. */}
        <DecisionChip decision={item.customerDecision} terminal={false} />
      </View>
    </View>
  );
};
